import { useMutation, UseMutationOptions, QueryListResult, QueryListFetchResult, useListQuery, UseQueryOptions, useQuery, QueryResult } from "t-lib-ui/connector";
import {
  RestPreferences,
  UserPreferences,
  RestUser,
  RestApiServer,
  RestFeedCreatorResult,
  RestFeeds,
  RestFeedPollRequest,
  RestFeedPollResult,
  RestFolderUpdate,
  RestReadMarkerPatch,
  RestPatchResult,
  RestUserFeed,
  RestSubscribe,
  RestSubscribeResult,
  RestCreateNewsletter,
  RestCreateNewsletterResult,
  RestRequestFeedByUrl,
  RestFeedStatus,
  PostStarPatch,
  RestList,
  RestAdminFeed,
  RestAdminFeedUpdate,
  RestAdminNotifications,
  RestCatalog,
  RestCatalogAdmin,
  RestCatalogCategories,
  RestCatalogCreate,
  RestCatalogUpdate,
  RestFeedDescriptionRequest,
  RestSpellcheckRequest,
  RestDescriptionRequest,
  RestFeedAliasRequest,
  RestFeedAliasResponse,
  RestAdminFeedDuplicates,
  RestAdminDuplicateGroups,
  RestAdminFeedMergeResult,
  RestCatalogAutocomplete,
  HomePageContent,
  HomeCategoryContent,
  DiscoverOnboarding,
  RestGenerateImage,
  RestUpscaleFeedIcon,
  RestUpscaleFeedIconResponse,
  RestUpscaleCustomImage,
  RestCustomImageFromUrl,
  SquareFit,
  RestStarList,
  RestUnreadFeed,
  RestSubscribeWithMode,
  RestFeedSummary,
  RestFeedSummariesRequest,
  RestOpmlImportRequest,
  RestOpmlImportResult,
  RestUserDataExport,
  RestContentReport,
  RestReport,
  RestReportPersonal,
  RestReportUpdate,
  ReportStatus,
  RestAnonFeeds,
  RestAnonPollResult,
  RestRecentUser
} from "../schema-copy/api-schema";
import { AccountStatus, Jurisdiction, RestAccountSummary, RestAdminAccountResult, RestDeletedUser, RestHoldRecord, RestInvitation, RestInvitationCreate, RestInvitationUpdate, RestRetentionLogEntry, RestTagSummary } from "../schema-copy/auth-schema";
import { useContext, useEffect } from "react";
import { AppState } from "../context/general";
import { authenticatedRequest, AuthData, getAdminMagicSecret, getApiHost, getCdnUrl, getCurrentAccount, getEmail, getJwt, unauthenticatedRequest } from "./auth";
import { nonNull } from "t-lib-ui/helpers";

export async function createUser(auth: AuthData, nickName: string): Promise<RestUser> {
  return await authenticatedRequest<RestUser>(auth, 'POST', 'api', `/api/user`, false, { nickName, tag: auth.tag || undefined });
}

/**
 * GET /api/feeds - Get feeds, folders, and read markers for current user
 */
export async function getReadMarkers(auth: AuthData): Promise<RestFeeds | null> {
  // Generous timeout: this is the boot-critical request (the whole library depends on it), its
  // payload scales with library size, and on mobile the default 6s is routinely exceeded while
  // the radio wakes up.
  return await authenticatedRequest<RestFeeds | null>(auth, 'GET', 'api', `/api/feeds`, false, undefined, 20000);
}

/**
 * GET /api/feed/:id - Get single feed by ID.
 * postTimes requests recentPostsSemi (recent post times for the auto-read limits).
 */
export async function getFeed(auth: AuthData, feedId: string, postTimes?: boolean): Promise<RestUserFeed> {
  return await authenticatedRequest<RestUserFeed>(auth, 'GET', 'api', `/api/feed/${feedId}${postTimes ? '?postTimes=1' : ''}`, false);
}

/**
 * POST /api/feeds/summaries - Batch fetch lightweight feed summaries (id, shortName, icon).
 * Used by FeedTile to render children/parent group references. Returns [] for empty input.
 */
export async function getFeedSummaries(auth: AuthData, feedIds: string[]): Promise<RestFeedSummary[]> {
  if (!feedIds.length) return [];
  const body: RestFeedSummariesRequest = { feedIds };
  return await authenticatedRequest<RestFeedSummary[]>(auth, 'POST', 'api', `/api/feeds/summaries`, false, body);
}

/**
 * POST /api/feed/poll - Poll for feed updates
 */
export async function pollFeeds(auth: AuthData, lastServerTime: number): Promise<RestFeedPollResult> {
  const body: RestFeedPollRequest = { lastServerTime };
  return await authenticatedRequest<RestFeedPollResult>(auth, 'POST', 'api', `/api/feed/poll`, false, body);
}

/**
 * POST /api/feeds/folders - Update folder structure for current user
 */
export async function updateFolders(auth: AuthData, folders: RestFolderUpdate): Promise<RestFolderUpdate> {
  return await authenticatedRequest<RestFolderUpdate>(auth, 'POST', 'api', `/api/feeds/folders`, false, folders);
}

/**
 * POST /api/feeds/markers - Apply patch to read markers for current user
 */
export async function patchReadMarkers(auth: AuthData, patch: RestReadMarkerPatch): Promise<RestPatchResult> {
  return await authenticatedRequest<RestPatchResult>(auth, 'POST', 'api', `/api/feeds/markers`, false, patch);
}

/**
 * POST /api/feed/subscribe - Subscribe to a feed
 */
export async function subscribeFeed(auth: AuthData, feedId: string): Promise<RestSubscribeResult> {
  const body: RestSubscribe = { feedId };
  return await authenticatedRequest<RestSubscribeResult>(auth, 'POST', 'api', `/api/feed/subscribe`, false, body);
}

/**
 * DELETE /api/feed/:id - Unsubscribe from a feed
 */
/**
 * POST /api/feed/subscribe-with-mode - Subscribe based on cached feed data
 */
export async function subscribeWithMode(auth: AuthData, url: string, mode: 'public' | 'private'): Promise<RestSubscribeResult> {
  const body: RestSubscribeWithMode = { url, mode };
  return await authenticatedRequest<RestSubscribeResult>(auth, 'POST', 'api', `/api/feed/subscribe-with-mode`, false, body);
}

/**
 * POST /api/newsletter - Create a newsletter (inbound-email) feed and auto-subscribe the creator.
 */
export async function createNewsletter(auth: AuthData, title: string): Promise<RestCreateNewsletterResult> {
  const body: RestCreateNewsletter = { title };
  return await authenticatedRequest<RestCreateNewsletterResult>(auth, 'POST', 'api', `/api/newsletter`, false, body);
}

export async function unsubscribeFeed(auth: AuthData, feedId: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(auth, 'DELETE', 'api', `/api/feed/${feedId}`, false);
}

/**
 * POST /api/feeds/starred - Patch starred posts
 */
export async function patchStarred(auth: AuthData, patch: PostStarPatch): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(auth, 'POST', 'api', `/api/feeds/starred`, false, patch);
}

/**
 * POST /api/feeds/readlater - Patch read-later posts
 */
export async function patchReadLater(auth: AuthData, patch: PostStarPatch): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(auth, 'POST', 'api', `/api/feeds/readlater`, false, patch);
}

/**
 * GET /api/feed/list/:id - Get starred or readlater list with feed metadata
 */
export async function getStarList(auth: AuthData, listId: 'starred' | 'readlater', count?: number, sortOrder?: 'newestFirst' | 'oldestFirst', ifModifiedSince?: string): Promise<RestStarList | null> {
  const params: any = {};
  if (count) params.count = count;
  if (sortOrder) params.sortOrder = sortOrder;
  if (ifModifiedSince) params.ifModifiedSince = ifModifiedSince;
  return await authenticatedRequest<RestStarList | null>(auth, 'GET', 'api', `/api/feed/list/${listId}`, false, params);
}

/**
 * GET /api/feed/unread[/:folder] - Get unread feed entries with feed metadata, optionally limited to one folder
 */
export async function getUnreadFeed(auth: AuthData, sortOrder?: 'newestFirst' | 'oldestFirst', count?: number, cursor?: string, ifModifiedSince?: string, folder?: string): Promise<RestUnreadFeed | null> {
  const params: any = {};
  if (sortOrder) params.sortOrder = sortOrder;
  if (count) params.count = count;
  if (cursor) params.cursor = cursor;
  if (ifModifiedSince) params.ifModifiedSince = ifModifiedSince;
  const path = folder != undefined ? `/api/feed/unread/${encodeURIComponent(folder)}` : '/api/feed/unread';
  return await authenticatedRequest<RestUnreadFeed | null>(auth, 'GET', 'api', path, false, params);
}

/**
 * POST /api/feed/request - Request a feed (creates if doesn't exist)
 * Returns RestUserFeed if feed is ACTIVE, RestFeedStatus otherwise
 */
export async function requestFeed(auth: AuthData, url: string): Promise<RestUserFeed | RestFeedStatus> {
  const body: RestRequestFeedByUrl = { url };
  return await authenticatedRequest<RestUserFeed | RestFeedStatus>(auth, 'POST', 'api', `/api/feed/request`, false, body);
}

/**
 * POST /api/opml/parse - Parse an OPML XML document into a flat list of feeds + folders.
 */
export async function parseOpml(auth: AuthData, xml: string): Promise<RestOpmlImportResult> {
  const body: RestOpmlImportRequest = { xml };
  return await authenticatedRequest<RestOpmlImportResult>(auth, 'POST', 'api', `/api/opml/parse`, false, body);
}

/**
 * GET /api/opml/export - Download user's subscriptions as an OPML file.
 * Triggers a browser download via a temporary <a> tag.
 */
export async function downloadOpml(auth: AuthData): Promise<void> {
  const res = await fetch(`${getApiHost(auth)}/api/opml/export`, {
    headers: { 'Authorization': `Bearer ${getJwt()}` },
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'downread-feeds.opml';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * GDPR Art. 20 — download all data the service holds about the current user as a
 * single JSON file. Combines the API's app-side export, the auth-server's
 * RestAccount, and the email decrypted from the locally-stored encrypted blob (getEmail).
 *
 * Plaintext email: lives ONLY in the user's browser. The auth server stores a
 * salted hash and cannot recover the plaintext, so if localStorage is empty we
 * simply omit the email field.
 *
 * Resilient: if the auth-server fetch fails, the file is still produced with
 * just the app-side data plus an `accountFetchFailed` note.
 */
export async function downloadUserData(auth: AuthData): Promise<void> {
  const [apiData, account] = await Promise.all([
    authenticatedRequest<RestUserDataExport>(auth, 'GET', 'api', '/api/user/export'),
    getCurrentAccount(auth).catch((e) => { console.warn('downloadUserData: auth-server fetch failed', e); return undefined; }),
  ]);

  const localEmail = await getEmail(auth).catch(() => undefined);

  const accountBlock = account ? {
    accountId: account.id,
    ...(localEmail ? { email: localEmail } : {}),
    nickName: account.nickName,
    ...(account.firstName ? { firstName: account.firstName } : {}),
    ...(account.lastName ? { lastName: account.lastName } : {}),
    authMethod: account.appleId ? 'apple' as const : account.googleId ? 'google' as const : 'password' as const,
    accountStatus: account.confirmed ? 'confirmed' as const : 'unconfirmed' as const,
    memberships: account.memberships,
  } : undefined;

  const notes: Record<string, string> = {
    feedMarkers:        "Encoded per-feed read state. Format: feedId/firstPostInBitmap/lastPostInBitmap/tailRead/sortOrder.",
    bitmaps:            "Base64-encoded bitmaps aligned 1:1 with feedMarkers, indicating which posts in the window are read.",
    starred:            "Entries are 'feedId-postIndex'; resolve feedId via the subscriptions array.",
    readLater:          "Same encoding as starred.",
    highestPostIndex:   "On each subscription. Highest valid post index for that feed at export time (= postCount - 1). Use it together with lastPublishedAt to align an importer's own per-feed indexes with the postIndex references in starred / readLater / feedMarkers / bitmaps.",
    lastPublishedAt:    "On each subscription. Publication date of the highest-indexed post, as ISO 8601 from the feed itself.",
  };
  if (localEmail)
    notes.email = "The email above was read from the browser's localStorage. The Downread auth server stores only a salted hash of the email, never the plaintext.";
  if (!account)
    notes.accountFetchFailed = "The auth-server fetch failed when this export was generated. The `account` block is therefore absent from this file.";

  const payload = {
    exportVersion: 1,
    generatedAt: new Date().toISOString(),
    service: 'downread',
    ...(accountBlock ? { account: accountBlock } : {}),
    profile: apiData.profile,
    subscriptions: apiData.subscriptions,
    readState: apiData.readState,
    _notes: notes,
  };

  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const a = document.createElement('a');
  a.href = url;
  a.download = `downread-data-${yyyy}-${mm}-${dd}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function usePutPreferences(options?: UseMutationOptions<UserPreferences>): (update: UserPreferences) => Promise<RestPreferences> {
  const appState = useContext(AppState);
  return useMutation((update: UserPreferences)=>authenticatedRequest<RestPreferences>(nonNull(appState.state.authData), 'PUT', 'api', `/api/preferences`, false, update),
    (update, result) => {
      return [
        [['preferences'], result]
      ];
    },
    {maxAgeMs: 600000, ...options as any});
}

/**
 * PUT /api/admin/feed/:id - Update feed admin fields
 * Requires magic authentication parameter
 */
export function usePutAdminFeed(feedId: string, magic: string, options?: UseMutationOptions<RestAdminFeedUpdate>): (update: RestAdminFeedUpdate) => Promise<RestAdminFeed> {
  const appState = useContext(AppState);
  return useMutation(
    async (update: RestAdminFeedUpdate) => {
      // First PUT the body, then append magic to the URL as query param
      const baseUrl = `/api/admin/feed/${feedId}?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'PUT',
        'api',
        baseUrl,
        false,
        update
      );
    },
    (update, result) => {
      return [
        [['admin-feed', feedId], result]
      ];
    },
    { maxAgeMs: 5000, ...options as any }
  );
}

/**
 * DELETE /api/admin/feed/:id - Hard-delete a BAD (disabled) feed. The backend only allows this for
 * feeds with no subscribers and no referenced archives (starred/read-later posts). Requires magic.
 */
export function useDeleteAdminFeed(feedId: string, magic: string): () => Promise<{ success: boolean }> {
  const appState = useContext(AppState);
  return useMutation(
    async (_: void) => {
      return await authenticatedRequest<{ success: boolean }>(
        nonNull(appState.state.authData),
        'DELETE',
        'api',
        `/api/admin/feed/${feedId}?magic=${encodeURIComponent(magic)}`,
        false
      );
    }
  );
}

/**
 * POST /api/admin/feed/:id/reprocess - Reset feed and trigger reprocessing
 */
export function useReprocessFeed(feedId: string, magic: string): () => Promise<RestAdminFeed> {
  const appState = useContext(AppState);
  return useMutation(
    async (_: void) => {
      const baseUrl = `/api/admin/feed/${feedId}/reprocess?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'POST',
        'api',
        baseUrl,
        false
      );
    },
    (_, result) => {
      return [
        [['admin-feed', feedId], result]
      ];
    },
    { maxAgeMs: 0 }
  );
}

/** What POST /api/admin/feed/:id/reset reports back. */
export interface ResetFeedResult {
  originalPostCount: number;
  /** ISO timestamp of the re-import fetch the reset scheduled (about a minute out). */
  nextFetchAt: string;
}

/**
 * POST /api/admin/feed/:id/reset - Drop the feed's reading state (post counters, dedup memory,
 * conditional validators, error streak) and schedule a re-import in a minute. The feed id,
 * subscriptions and catalog entry survive.
 */
export function useResetFeed(feedId: string, magic: string): () => Promise<ResetFeedResult> {
  const appState = useContext(AppState);
  return useMutation(
    async (_: void) => {
      const baseUrl = `/api/admin/feed/${feedId}/reset?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<ResetFeedResult>(
        nonNull(appState.state.authData),
        'POST',
        'api',
        baseUrl,
        false,
        undefined,
        300_000
      );
    },
    // The feed row changed server-side; drop the cached copy so the page refetches it.
    () => [[['admin-feed', feedId], undefined]],
    { maxAgeMs: 0 }
  );
}

/** RestAdminFeedDuplicates tagged with the feed it was fetched for, so callers can tell a stale
 * result (still showing while the next feed's request is in flight) from the current one. */
export type FeedDuplicates = RestAdminFeedDuplicates & { feedId: string };

/**
 * GET /api/admin/feed/:id/duplicates - List other feeds sharing this feed's siteUrl, and up to 10
 * other public feeds on the same site domain. Follows feedId changes: the admin pages stay mounted
 * while navigating from one feed to the next.
 */
export function useGetFeedDuplicates(feedId: string, magic: string, options?: UseQueryOptions<FeedDuplicates>): QueryResult<FeedDuplicates, string> {
  const appState = useContext(AppState);
  const query = useQuery<FeedDuplicates, string>(
    id => ['admin-feed-duplicates', id],
    feedId,
    async id => ({
      ...await authenticatedRequest<RestAdminFeedDuplicates>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        `/api/admin/feed/${id}/duplicates`,
        false,
        { magic }
      ),
      feedId: id
    }),
    { maxAgeMs: 30000, enabled: !!magic && !!feedId && feedId != 'next', ...options as any }
  );
  const { changeInput } = query;
  useEffect(() => changeInput(feedId), [feedId]); // eslint-disable-line react-hooks/exhaustive-deps
  return query;
}

/**
 * GET /api/admin/feed/next - Get next feed for manual review
 * Requires magic authentication parameter
 */
export function useGetNextFeedForReview(magic: string, options?: UseMutationOptions<RestAdminFeed>): () => Promise<RestAdminFeed> {
  const appState = useContext(AppState);
  return useMutation(
    async (_: void) => {
      return await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        `/api/admin/feed/next`,
        false,
        { magic }
      );
    },
    (_, result) => {
      return [
        [['next-feed-review'], result]
      ];
    },
    { maxAgeMs: 0, ...options as any }
  );
}

// --- Admin Feeds API ---

export type FeedListType = 'unverified' | 'new' | 'popular' | 'problem' | 'bad';

export interface GetAdminFeedListParams {
  listType: FeedListType;
  name?: string;
  reverse?: boolean;
}

/**
 * GET /api/admin/feeds/:list - Get paginated list of feeds for admin
 * Requires magic authentication parameter. Fetches nothing while not enabled.
 */
export function useGetAdminFeedList(chunkSize: number, queryInit: GetAdminFeedListParams, magic: string, enabled = true): QueryListResult<RestList<RestAdminFeed>, GetAdminFeedListParams> {
  const appState = useContext(AppState);
  return useListQuery<RestList<RestAdminFeed>, GetAdminFeedListParams>(
    chunkSize,
    queryInit,
    (query, limit, last) => ['admin-feeds', query.listType, query.name ?? '', query.reverse?.toString() ?? 'false', last ?? '', limit.toString()],
    async (query, limit, last): Promise<QueryListFetchResult<RestList<RestAdminFeed>>> => {
      const params: any = { ...query, limit, last, magic: getAdminMagicSecret() };
      const r = await authenticatedRequest<RestList<RestAdminFeed>>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        `/api/admin/feeds/${query.listType}`,
        false,
        params
      );
      return { chunk: r, hasMore: r.hasMore, cursor: r.cursor };
    },
    {
      maxAgeMs: 5000,
      enabled: enabled && !!getAdminMagicSecret() // Only fetch when magic is not empty
    }
  );
}

/** What the admin duplicate groups share: the site or the shown name. */
export type DuplicateGroupBy = 'site' | 'name';

/**
 * GET /api/admin/feed-duplicates - Public feeds pointing to the same site (or having the same shown
 * name), grouped, the group with the newest feed first. Requires the magic authentication parameter.
 */
export function useGetAdminDuplicateFeeds(chunkSize: number, by: DuplicateGroupBy, magic: string): QueryListResult<RestAdminDuplicateGroups, {}> {
  const appState = useContext(AppState);
  return useListQuery<RestAdminDuplicateGroups, {}>(
    chunkSize,
    {},
    (_query, limit, last) => ['admin-feed-duplicates-list', by, last ?? '', limit.toString()],
    async (_query, limit, last): Promise<QueryListFetchResult<RestAdminDuplicateGroups>> => {
      const r = await authenticatedRequest<RestAdminDuplicateGroups>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        '/api/admin/feed-duplicates',
        false,
        { limit, last, by, magic: getAdminMagicSecret() }
      );
      return { chunk: r, hasMore: r.hasMore, cursor: r.cursor };
    },
    { maxAgeMs: 0, enabled: !!magic }
  );
}

/**
 * POST /api/admin/feed/:id/merge - Unite a duplicate feed with another feed of the same site: its
 * aliases move to intoFeedId, then it is deleted. The server refuses unless the duplicate has no
 * subscribers, no referenced archives and no catalog entry.
 */
/** PUT /api/admin/feed/:id - like usePutAdminFeed, for a changing set of feeds. */
export async function putAdminFeed(auth: AuthData, feedId: string, update: RestAdminFeedUpdate, magic: string): Promise<RestAdminFeed> {
  return await authenticatedRequest<RestAdminFeed>(auth, 'PUT', 'api', `/api/admin/feed/${feedId}?magic=${encodeURIComponent(magic)}`, false, update);
}

export async function mergeAdminFeed(auth: AuthData, feedId: string, intoFeedId: string, magic: string): Promise<RestAdminFeedMergeResult> {
  return await authenticatedRequest<RestAdminFeedMergeResult>(
    auth, 'POST', 'api', `/api/admin/feed/${feedId}/merge?magic=${encodeURIComponent(magic)}`, false, { intoFeedId });
}

// --- Catalog API ---

/**
 * GET /api/catalog/:feedId - Get catalog entry by feed ID
 */
export async function getCatalog(auth: AuthData, feedId: string): Promise<RestCatalog | RestCatalogAdmin> {
  return await authenticatedRequest<RestCatalog | RestCatalogAdmin>(auth, 'GET', 'api', `/api/catalog/${feedId}`, false);
}

/**
 * GET /api/public/feed/:id - Get feed by ID with catalog if available (public access)
 * Returns RestCatalog if catalog exists and is active, RestUserFeed otherwise
 * Uses authenticated request if auth data is available, otherwise unauthenticated request.
 */
export async function getPublicFeed(feedId: string, auth?: AuthData): Promise<RestUserFeed | RestCatalog> {
  const encodedId = encodeURIComponent(feedId);
  if (auth)
    return await authenticatedRequest<RestUserFeed | RestCatalog>(auth, 'GET', 'api', `/api/public/feed/${encodedId}`, false);
  return await unauthenticatedRequest<RestUserFeed | RestCatalog>('GET', 'api', `/api/public/feed/${encodedId}`);
}

/** Several public feeds by id (one getPublicFeed each, in parallel); feeds that fail to load are left out. */
export function useGetPublicFeeds(feedIds: string[], auth?: AuthData, options?: UseQueryOptions<(RestUserFeed | RestCatalog)[]>): QueryResult<(RestUserFeed | RestCatalog)[], string[]> {
  return useQuery<(RestUserFeed | RestCatalog)[], string[]>(
    ids => ['public-feeds', ...ids],
    feedIds,
    async ids => (await Promise.all(ids.map(id => getPublicFeed(id, auth).catch(() => undefined))))
      .filter((f): f is RestUserFeed | RestCatalog => !!f),
    { maxAgeMs: 3600000, ...options as any }
  );
}

/**
 * GET /api/public/feed-by-url?url=... - Look up an EXISTING feed by its RSS/Atom URL (public access).
 * Returns RestCatalog if catalog exists and is active, RestUserFeed otherwise. Throws if the feed
 * does not exist or is private (the server never fetches or creates feeds here).
 * Uses authenticated request if auth data is available, otherwise unauthenticated request.
 */
export async function getPublicFeedByUrl(url: string, auth?: AuthData): Promise<RestUserFeed | RestCatalog> {
  const path = `/api/public/feed-by-url?url=${encodeURIComponent(url)}`;
  if (auth)
    return await authenticatedRequest<RestUserFeed | RestCatalog>(auth, 'GET', 'api', path, false);
  return await unauthenticatedRequest<RestUserFeed | RestCatalog>('GET', 'api', path);
}

/**
 * POST /api/anon/feeds - anonymous (no-login) feed list. Returns feed metadata (with capped
 * recentPostsSemi + baseUrl per feed) for the client-supplied feed ids. Unauthenticated.
 */
export async function getAnonFeeds(feedIds: string[], seq?: number): Promise<RestAnonFeeds> {
  return await unauthenticatedRequest<RestAnonFeeds>('POST', 'api', `/api/anon/feeds`, seq === undefined ? { feedIds } : { feedIds, seq });
}

// Coalesce bursts of single-feed /api/anon/feeds lookups (e.g. a bulk subscribe firing one per feed):
// the first request opens a 30ms window, every call within it adds its ids, then a SINGLE getAnonFeeds
// is issued for the union and each caller is handed back only the feeds it asked for.
const ANON_FEEDS_BATCH_MS = 30;
let anonFeedsPending: Set<string> | null = null;
let anonFeedsBatchPromise: Promise<RestAnonFeeds> | null = null;
let anonFeedsBatchSeq = 0;

export function getAnonFeedsBatched(feedIds: string[], seq?: number): Promise<RestAnonFeeds> {
  if (!anonFeedsPending) anonFeedsPending = new Set();
  feedIds.forEach(id => anonFeedsPending!.add(id));
  if (!anonFeedsBatchPromise) {
    anonFeedsBatchPromise = new Promise<RestAnonFeeds>((resolve, reject) => {
      setTimeout(() => {
        const ids = Array.from(anonFeedsPending!);
        anonFeedsPending = null;
        anonFeedsBatchPromise = null;
        getAnonFeeds(ids, anonFeedsBatchSeq++).then(resolve, reject);
      }, ANON_FEEDS_BATCH_MS);
    });
  }
  const requested = new Set(feedIds);
  return anonFeedsBatchPromise.then(result => ({
    ...result,
    feeds: result.feeds.filter(f => requested.has(f.id)),
  }));
}

/**
 * POST /api/anon/poll - anonymous (no-login) poll. Returns per-feed deltas since lastServerTime
 * plus newest post-times for changed feeds. Unauthenticated.
 */
export async function pollAnonFeeds(feedIds: string[], lastServerTime: number): Promise<RestAnonPollResult> {
  return await unauthenticatedRequest<RestAnonPollResult>('POST', 'api', `/api/anon/poll`, { feedIds, lastServerTime });
}

/**
 * POST /api/report - submit a content report (notice-and-action; see LEGAL.md).
 * Works logged-out (/a/feed preview) as well as logged-in; the server captures the
 * reporter's user id automatically when authenticated.
 */
export async function postReport(report: RestContentReport, auth?: AuthData): Promise<{ success: boolean }> {
  if (auth)
    return await authenticatedRequest<{ success: boolean }>(auth, 'POST', 'api', `/api/report`, false, report);
  return await unauthenticatedRequest<{ success: boolean }>('POST', 'api', `/api/report`, report);
}

/**
 * GET /api/catalog/:feedId - Get catalog entry without authentication (public access)
 * Uses authenticated request if auth data is available, otherwise unauthenticated request.
 */
export async function getPublicCatalog(feedId: string, auth?: AuthData): Promise<RestCatalog> {
  const encodedId = encodeURIComponent(feedId);
  if (auth)
    return await authenticatedRequest<RestCatalog>(auth, 'GET', 'api', `/api/catalog/${encodedId}`, false);
  return await unauthenticatedRequest<RestCatalog>('GET', 'api', `/api/catalog/${encodedId}`);
}

/**
 * GET /api/catalog/categories - Get all catalog categories
 */
export async function getCatalogCategories(auth: AuthData): Promise<RestCatalogCategories> {
  return await authenticatedRequest<RestCatalogCategories>(auth, 'GET', 'api', `/api/catalog/categories`, false);
}

/**
 * GET /api/catalog/autocomplete - Fast autocomplete search
 */
export async function getAutocomplete(auth: AuthData | undefined, query: string): Promise<RestCatalogAutocomplete> {
  return await publicOrAuthGet<RestCatalogAutocomplete>(auth, `/api/catalog/autocomplete`, { q: query });
}

/**
 * GET /api/catalog/categories - Get all catalog categories (hook)
 */
// GET a public endpoint, authenticated when a user is logged in and unauthenticated otherwise.
// Used for the catalog browse/search/categories endpoints, which the backend serves without auth
// so anonymous (no-login) users can discover feeds. `params` become the query string for GET.
function publicOrAuthGet<T>(auth: AuthData | undefined, path: string, params?: any): Promise<T> {
  return auth
    ? authenticatedRequest<T>(auth, 'GET', 'api', path, false, params)
    : unauthenticatedRequest<T>('GET', 'api', path, params);
}

export function useGetCatalogCategories(options?: UseQueryOptions<RestCatalogCategories>): QueryResult<RestCatalogCategories, undefined> {
  const appState = useContext(AppState);
  return useQuery<RestCatalogCategories, undefined>(
    ['catalog-categories'],
    undefined,
    async () => publicOrAuthGet<RestCatalogCategories>(appState.state.authData, `/api/catalog/categories`),
    { maxAgeMs: 7200000, useLocalStorage: true, ...options as any } // Cache for 1 hour
  );
}

/**
 * GET /api/catalog/category/:category - Get catalog entries by category with cursor-based pagination
 */
export function useGetCatalogByCategory(chunkSize: number, category?: string, options?: UseQueryOptions<RestList<RestCatalog>>): QueryListResult<RestList<RestCatalog>, { category?: string }> {
  const appState = useContext(AppState);
  return useListQuery<RestList<RestCatalog>, { category?: string }>(
    chunkSize,
    category ? { category } : {},
    (query, limit, last) => ['catalog-category', query.category ?? 'all', last ?? '', limit.toString()],
    async (query, limit, last): Promise<QueryListFetchResult<RestList<RestCatalog>>> => {
      const params: any = { limit, last };
      const r = await publicOrAuthGet<RestList<RestCatalog>>(
        appState.state.authData,
        `/api/catalog/category/${encodeURIComponent(query.category ?? 'all')}`,
        params
      );
      return { chunk: r, hasMore: r.hasMore, cursor: r.cursor };
    },
    { maxAgeMs: 3600000, useLocalStorage: !category, ...options as any }
  );
}

/**
 * GET /api/catalog/search - Search catalog entries with cursor-based pagination
 */
export function useSearchCatalog(chunkSize: number, searchQuery: string, options?: UseQueryOptions<RestList<RestCatalog>>): QueryListResult<RestList<RestCatalog>, { query: string }> {
  const appState = useContext(AppState);
  return useListQuery<RestList<RestCatalog>, { query: string }>(
    chunkSize,
    { query: searchQuery },
    (query, limit, last) => ['catalog-search', query.query, last ?? '', limit.toString()],
    async (query, limit, last): Promise<QueryListFetchResult<RestList<RestCatalog>>> => {
      const params: any = { query: query.query, limit, last };
      const r = await publicOrAuthGet<RestList<RestCatalog>>(appState.state.authData, `/api/catalog/search`, params);
      return { chunk: r, hasMore: r.hasMore, cursor: r.cursor };
    },
    { maxAgeMs: 60000, ...options as any }
  );
}

/**
 * POST /api/admin/catalog - Create a new catalog entry
 * Requires magic authentication parameter
 */
export function usePostAdminCatalog(magic: string, options?: UseMutationOptions<RestCatalogCreate>): (create: RestCatalogCreate) => Promise<RestCatalogAdmin> {
  const appState = useContext(AppState);
  return useMutation(
    async (create: RestCatalogCreate) => {
      const url = `/api/admin/catalog?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<RestCatalogAdmin>(
        nonNull(appState.state.authData),
        'POST',
        'api',
        url,
        false,
        create
      );
    },
    (create, result) => {
      return [
        [['admin-catalog', create.feedId], result]
      ];
    },
    { maxAgeMs: 5000, ...options as any }
  );
}

/**
 * PUT /api/admin/catalog/:feedId - Update a catalog entry
 * Requires magic authentication parameter
 */
export function usePutAdminCatalog(feedId: string, magic: string, options?: UseMutationOptions<RestCatalogUpdate>): (update: RestCatalogUpdate) => Promise<RestCatalogAdmin> {
  const appState = useContext(AppState);
  return useMutation(
    async (update: RestCatalogUpdate) => {
      const url = `/api/admin/catalog/${feedId}?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<RestCatalogAdmin>(
        nonNull(appState.state.authData),
        'PUT',
        'api',
        url,
        false,
        update
      );
    },
    (update, result) => {
      return [
        [['admin-catalog', feedId], result]
      ];
    },
    { maxAgeMs: 5000, ...options as any }
  );
}

/**
 * DELETE /api/admin/catalog/:feedId - Delete a catalog entry
 * Requires magic authentication parameter
 */
export function useDeleteAdminCatalog(feedId: string, magic: string, options?: UseMutationOptions<void>): () => Promise<{ success: boolean }> {
  const appState = useContext(AppState);
  return useMutation(
    async (_: void) => {
      const url = `/api/admin/catalog/${feedId}?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<{ success: boolean }>(
        nonNull(appState.state.authData),
        'DELETE',
        'api',
        url,
        false
      );
    },
    (_, result) => {
      return [
        [['admin-catalog', feedId], null] // Clear from cache
      ];
    },
    { maxAgeMs: 0, ...options as any }
  );
}

/**
 * POST /api/admin/feed-description - Generate feed description using AI
 * Requires magic authentication parameter
 */
export async function generateFeedDescription(auth: AuthData, feedId: string, extraInstructions?: string): Promise<{ description: string; model: string }> {
  const magic = getAdminMagicSecret();
  const body: RestFeedDescriptionRequest = { feedId, extraInstructions };
  return await authenticatedRequest<{ description: string; model: string }>(
    auth,
    'POST',
    'api',
    `/api/admin/feed-description?magic=${encodeURIComponent(magic || '')}`,
    false,
    body,
    30000  // 30 second timeout for AI requests
  );
}

/**
 * POST /api/admin/spellcheck - Spellcheck and improve text using AI
 * Requires magic authentication parameter
 */
export async function spellcheckText(auth: AuthData, text: string, extraInstructions?: string): Promise<string> {
  const magic = getAdminMagicSecret();
  const body: RestSpellcheckRequest = { text, extraInstructions };
  return await authenticatedRequest<string>(
    auth,
    'POST',
    'api',
    `/api/admin/spellcheck?magic=${encodeURIComponent(magic || '')}`,
    false,
    body,
    30000  // 30 second timeout for AI requests
  );
}

/**
 * POST /api/admin/description - Improve description text using AI
 * Requires magic authentication parameter
 */
export async function generateDescription(auth: AuthData, description: string, extraInstructions?: string): Promise<string> {
  const magic = getAdminMagicSecret();
  const body: RestDescriptionRequest = { description, extraInstructions };
  return await authenticatedRequest<string>(
    auth,
    'POST',
    'api',
    `/api/admin/description?magic=${encodeURIComponent(magic || '')}`,
    false,
    body,
    30000  // 30 second timeout for AI requests
  );
}

/**
 * POST /api/admin/feed/alias - Check if a URL is an alias for a feed
 * Requires magic authentication parameter
 */
export function useCheckFeedAlias(magic: string, options?: UseMutationOptions<RestFeedAliasRequest>): (request: RestFeedAliasRequest) => Promise<RestFeedAliasResponse> {
  const appState = useContext(AppState);
  return useMutation(
    async (request: RestFeedAliasRequest) => {
      const url = `/api/admin/feed/alias?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<RestFeedAliasResponse>(
        nonNull(appState.state.authData),
        'POST',
        'api',
        url,
        false,
        request
      );
    },
    (request, result) => {
      return [
        [['feed-alias-check', request.feedId, request.url], result]
      ];
    },
    { maxAgeMs: 5000, ...options as any }
  );
}

/**
 * PUT /api/admin/feed/alias - Add URL alias for a feed
 * Requires magic authentication parameter
 */
export function useSetFeedAlias(magic: string, options?: UseMutationOptions<RestFeedAliasRequest>): (request: RestFeedAliasRequest) => Promise<RestFeedAliasResponse> {
  const appState = useContext(AppState);
  return useMutation(
    async (request: RestFeedAliasRequest) => {
      const url = `/api/admin/feed/alias?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<RestFeedAliasResponse>(
        nonNull(appState.state.authData),
        'PUT',
        'api',
        url,
        false,
        request
      );
    },
    (request, result) => {
      return [
        [['feed-alias-check', request.feedId, request.url], result]
      ];
    },
    { maxAgeMs: 5000, ...options as any }
  );
}

/**
 * DELETE /api/admin/feed/alias - Remove an alias from a feed.
 * Set pending=true to remove an unpublished (pending) alias instead of a published one.
 * The alias must be passed exactly as displayed (normalized, scheme-less).
 * Requires magic authentication parameter.
 */
export function useDeleteFeedAlias(magic: string): (request: { feedId: string; alias: string; pending?: boolean }) => Promise<{ success: boolean }> {
  const appState = useContext(AppState);
  return useMutation(
    async ({ feedId, alias, pending }: { feedId: string; alias: string; pending?: boolean }) => {
      // DELETE sends its arguments as query params, so magic travels alongside them.
      return await authenticatedRequest<{ success: boolean }>(
        nonNull(appState.state.authData),
        'DELETE',
        'api',
        `/api/admin/feed/alias`,
        false,
        { magic, feedId, alias, pending: pending ? 'true' : undefined }
      );
    }
  );
}

/**
 * GET onboarding collections from CDN (discover/onboarding.json).
 * Fixed (non-date) file; pass { enabled } to fetch lazily.
 */
export function useGetDiscoverOnboarding(options?: UseQueryOptions<DiscoverOnboarding>): QueryResult<DiscoverOnboarding, undefined> {
  const appState = useContext(AppState);
  return useQuery<DiscoverOnboarding, undefined>(
    ['discover-onboarding'],
    undefined,
    async () => {
      const cdnUrl = getCdnUrl(appState.state.authData);
      // Regenerated hourly server-side; bust the CDN cache once per hour.
      const hourBucket = Math.floor(Date.now() / 3600000);
      const response = await fetch(`${cdnUrl}discover/onboarding.json?t=${hourBucket}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch onboarding content: ${response.statusText}`);
      }
      return await response.json();
    },
    { maxAgeMs: 3600000, ...options as any } // Cache for 1 hour
  );
}

/** Eastern-time date (YYYYMMDD) `daysAgo` days back, with the same fixed EST offset as the server. */
const easternDate = (daysAgo = 0) =>
  new Date(Date.now() - 5 * 3600000 - daysAgo * 86400000).toISOString().slice(0, 10).replace(/-/g, '');

/**
 * GET the home page content (Discover categories, stats) from the CDN at home/page-{date}.json.
 * @param date - YYYYMMDD from RestFeeds.homeDate. Without one (anonymous mode never asks the server)
 *   today's file is tried, then yesterday's (today's may not be published yet right after midnight).
 */
export function useGetHomeContent(date?: string, options?: UseQueryOptions<HomePageContent>): QueryResult<HomePageContent, { date: string }> {
  const appState = useContext(AppState);
  return useQuery<HomePageContent, { date: string }>(
    ['home-content', date ?? 'latest'],
    { date: date ?? '' },
    async () => {
      const cdnUrl = getCdnUrl(appState.state.authData);
      // The day's file is regenerated hourly (stats, feed properties); bust the CDN cache once per hour.
      const hourBucket = Math.floor(Date.now() / 3600000);
      for (const d of date ? [date] : [easternDate(), easternDate(1)]) {
        const response = await fetch(`${cdnUrl}home/page-${d}.json?t=${hourBucket}`);
        if (response.ok) return await response.json();
        if (response.status != 404) throw new Error(`Failed to fetch home content: ${response.statusText}`);
      }
      throw new Error('No home content published');
    },
    { maxAgeMs: 3600000, ...options as any } // Cache for 1 hour
  );
}

/**
 * GET one Home category with all its feeds (the "See all" page) from the CDN at
 * home/category/{slug}-{date}.json. `date` is the date of the home/page it belongs to
 * (HomePageContent.date); nothing is fetched until it is known.
 */
export function useGetHomeCategory(slug: string, date?: string): QueryResult<HomeCategoryContent, { slug: string, date: string }> {
  const appState = useContext(AppState);
  return useQuery<HomeCategoryContent, { slug: string, date: string }>(
    ['home-category', slug, date ?? ''],
    { slug, date: date ?? '' },
    async () => {
      // Regenerated hourly (feed properties); bust the CDN cache once per hour like home/page.
      const hourBucket = Math.floor(Date.now() / 3600000);
      const response = await fetch(`${getCdnUrl(appState.state.authData)}home/category/${slug}-${date}.json?t=${hourBucket}`);
      if (!response.ok) throw new Error(`Failed to fetch home category: ${response.statusText}`);
      return await response.json();
    },
    { maxAgeMs: 3600000, enabled: !!date } // Cache for 1 hour
  );
}

// --- Stuff API (Admin) ---

/**
 * GET /api/admin/stuff/:name - Get stuff by name
 * Requires magic authentication parameter
 */
export function useGetStuff<T = any>(name: string, magic: string, options?: UseQueryOptions<T>): QueryResult<T, undefined> {
  const appState = useContext(AppState);
  return useQuery<T, undefined>(
    ['admin-stuff', name],
    undefined,
    async () => {
      const url = `/api/admin/stuff/${encodeURIComponent(name)}?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<T>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        url,
        false
      );
    },
    { maxAgeMs: 5000, enabled: !!magic, ...options as any }
  );
}

/**
 * PUT /api/admin/stuff/:name - Set stuff by name
 * Requires magic authentication parameter
 */
// ⚠️ Using a new stuff `name` here? Add it to downread/STUFF.md (the stuff inventory).
export function useSetStuff<T = any>(name: string, magic: string, options?: UseMutationOptions<T>): (data: T) => Promise<{ version: number }> {
  const appState = useContext(AppState);
  return useMutation(
    async (data: T) => {
      const url = `/api/admin/stuff/${encodeURIComponent(name)}?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<{ version: number }>(
        nonNull(appState.state.authData),
        'PUT',
        'api',
        url,
        false,
        data
      );
    },
    (data) => {
      return [
        [['admin-stuff', name], data]
      ];
    },
    { maxAgeMs: 0, ...options as any }
  );
}

/**
 * POST /api/admin/upload/image - Upload image to CDN
 * Requires magic authentication parameter
 */
export function useUploadImage(magic: string, options?: UseMutationOptions<{ image: File; name: string }>): (data: { image: File; name: string }) => Promise<{ name: string; url: string }> {
  const appState = useContext(AppState);
  return useMutation(
    async (data: { image: File; name: string }) => {
      const url = `/api/admin/upload/image?magic=${encodeURIComponent(magic)}`;

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('image', data.image);
      formData.append('name', data.name);

      // Use fetch directly since authenticatedRequest may not handle FormData
      const authData = nonNull(appState.state.authData);
      const fullUrl = `${getApiHost(authData)}${url}`;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getJwt()}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      return await response.json();
    },
    () => {
      // Invalidate image-collection cache to refresh list
      return [
        [['admin-stuff', 'image-collection'], null]
      ];
    },
    { maxAgeMs: 0, ...options as any }
  );
}

/**
 * POST /api/admin/generate-image - Generate image using AI
 * Requires magic authentication parameter
 */
export function useGenerateImage(magic: string, options?: UseMutationOptions<RestGenerateImage>): (request: RestGenerateImage) => Promise<{ name: string; url: string }> {
  const appState = useContext(AppState);
  return useMutation(
    async (request: RestGenerateImage) => {
      const url = `/api/admin/generate-image?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<{ name: string; url: string }>(
        nonNull(appState.state.authData),
        'POST',
        'api',
        url,
        false,
        request,
        120000  // 2 minute timeout for image generation
      );
    },
    () => {
      // Invalidate image-collection cache to refresh list
      return [
        [['admin-stuff', 'image-collection'], null]
      ];
    },
    { maxAgeMs: 0, ...options as any }
  );
}

/**
 * POST /api/admin/feed/:id/custom-image/upload - Upload a custom logo (raster or SVG) for a feed.
 * Multipart; returns the updated RestAdminFeed. fit says how a non-square raster image is squared.
 */
export async function uploadCustomImage(auth: AuthData, feedId: string, image: File, fit: SquareFit = 'pad'): Promise<RestAdminFeed> {
  const magic = getAdminMagicSecret() || '';
  const formData = new FormData();
  formData.append('image', image);
  const response = await fetch(`${getApiHost(auth)}/api/admin/feed/${feedId}/custom-image/upload?magic=${encodeURIComponent(magic)}&fit=${fit}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${getJwt()}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Upload failed');
  }
  return await response.json();
}

/**
 * POST /api/admin/feed/:id/custom-image/upscale - AI-upscale a source image into the feed's custom
 * image (192px max). Returns the updated RestAdminFeed.
 */
export async function upscaleCustomImage(auth: AuthData, feedId: string, request: RestUpscaleCustomImage): Promise<RestAdminFeed> {
  const magic = getAdminMagicSecret() || '';
  return await authenticatedRequest<RestAdminFeed>(
    auth,
    'POST',
    'api',
    `/api/admin/feed/${feedId}/custom-image/upscale?magic=${encodeURIComponent(magic)}`,
    false,
    request,
    120000
  );
}

/**
 * POST /api/admin/feed/:id/custom-image/url - Download an image from a public URL (e.g. a logo on the
 * feed's site) into the feed's custom image. Returns the updated RestAdminFeed.
 */
export async function customImageFromUrl(auth: AuthData, feedId: string, request: RestCustomImageFromUrl): Promise<RestAdminFeed> {
  const magic = getAdminMagicSecret() || '';
  return await authenticatedRequest<RestAdminFeed>(
    auth,
    'POST',
    'api',
    `/api/admin/feed/${feedId}/custom-image/url?magic=${encodeURIComponent(magic)}`,
    false,
    request,
    30000
  );
}

/**
 * POST /api/admin/feed-icon/upscale - Upscale a feed icon to a crisp 96x96 PNG and upload as logo
 */
export async function upscaleFeedIcon(auth: AuthData, request: RestUpscaleFeedIcon): Promise<RestUpscaleFeedIconResponse> {
  const magic = getAdminMagicSecret();
  return await authenticatedRequest<RestUpscaleFeedIconResponse>(
    auth,
    'POST',
    'api',
    `/api/admin/feed-icon/upscale?magic=${encodeURIComponent(magic || '')}`,
    false,
    request,
    120000
  );
}

/**
 * POST /api/admin/scale/image/:name - Regenerate scaled versions of an existing image
 * Requires magic authentication parameter
 */
export function useScaleImage(magic: string, options?: UseMutationOptions<string>): (imageName: string) => Promise<{}> {
  const appState = useContext(AppState);
  return useMutation(
    async (imageName: string) => {
      const url = `/api/admin/scale/image/${encodeURIComponent(imageName)}?magic=${encodeURIComponent(magic)}`;
      return await authenticatedRequest<{ name: string; url: string; versions: string[] }>(
        nonNull(appState.state.authData),
        'POST',
        'api',
        url,
        false
      );
    },
    () => {
      // No cache invalidation needed since this doesn't change the image list
      return [];
    },
    { maxAgeMs: 0, ...options as any }
  );
}

/**
 * GET /admin/account/email - Lookup account by email from auth service
 * Requires magic authentication parameter
 */
export async function lookupAccountByEmail(auth: AuthData, email: string, magic: string): Promise<RestAdminAccountResult> {
  return await authenticatedRequest<RestAdminAccountResult>(
    auth,
    'GET',
    'auth',
    `/auth/admin/account/email?email=${encodeURIComponent(email)}&magic=${encodeURIComponent(magic)}`,
    false
  );
}

/**
 * GET /admin/account/id/:id - Lookup account by ID from auth service
 * Requires magic authentication parameter
 */
export async function lookupAccountById(auth: AuthData, accountId: string, magic: string): Promise<RestAdminAccountResult> {
  return await authenticatedRequest<RestAdminAccountResult>(
    auth,
    'GET',
    'auth',
    `/auth/admin/account/id/${encodeURIComponent(accountId)}?magic=${encodeURIComponent(magic)}`,
    false
  );
}

/**
 * GET /api/admin/user/account/:accountId - Get user by account ID from Downread API
 * Requires magic authentication parameter
 */
/** GET /api/admin/user/account/:accountId - fetch the Downread user for an auth account (admin only).
 *  The user record lives on the account's jurisdiction instance (EU users are only on e1), so pass
 *  apiHostOverride to target that server; omit to use the default host. */
export async function getUserByAccountId(auth: AuthData, accountId: string, magic: string, apiHostOverride?: string): Promise<RestUser> {
  return await authenticatedRequest<RestUser>(
    auth,
    'GET',
    'api',
    `/api/admin/user/account/${encodeURIComponent(accountId)}?magic=${encodeURIComponent(magic)}`,
    false,
    undefined,
    undefined,
    apiHostOverride
  );
}

/** GET /api/admin/users/recent - the most recently created users on the targeted API instance (admin
 *  only), newest first. createdAt + tag come from the API user; use adminGetAccountSummaries to
 *  annotate admin flag + last login. apiHostOverride targets a specific instance (e.g. EU/e1); omit
 *  for the default host. */
export async function adminListRecentUsers(auth: AuthData, magic: string, limit = 10, apiHostOverride?: string): Promise<RestRecentUser[]> {
  return await authenticatedRequest<RestRecentUser[]>(
    auth,
    'GET',
    'api',
    `/api/admin/users/recent`,
    false,
    { magic, limit },
    undefined,
    apiHostOverride
  );
}

/** GET /auth/admin/accounts/summary - admin flag + last login for a batch of account ids (auth
 *  service, admin only). Annotates the recent-users list without a full per-account lookup. */
export async function adminGetAccountSummaries(auth: AuthData, accountIds: string[], magic: string): Promise<RestAccountSummary[]> {
  if (!accountIds.length) return [];
  return await authenticatedRequest<RestAccountSummary[]>(
    auth,
    'GET',
    'auth',
    `/auth/admin/accounts/summary?ids=${encodeURIComponent(accountIds.join(','))}&magic=${encodeURIComponent(magic)}`,
    false
  );
}

/** GET /api/admin/api-servers - list the configured API server instances (admin only). The list is
 *  the same on every instance, so this uses the default API host. */
export async function adminGetApiServers(auth: AuthData, magic: string): Promise<RestApiServer[]> {
  return await authenticatedRequest<RestApiServer[]>(
    auth,
    'GET',
    'api',
    `/api/admin/api-servers?magic=${encodeURIComponent(magic)}`,
    false
  );
}

/** GET /api/admin/feed/:id/creator - find who created a feed, as seen by a specific API server
 *  (admin only). apiHostOverride targets the selected server's users DB; omit to use the default. */
export async function adminGetFeedCreator(auth: AuthData, feedId: string, magic: string, apiHostOverride?: string): Promise<RestFeedCreatorResult> {
  return await authenticatedRequest<RestFeedCreatorResult>(
    auth,
    'GET',
    'api',
    `/api/admin/feed/${encodeURIComponent(feedId)}/creator?magic=${encodeURIComponent(magic)}`,
    false,
    undefined,
    undefined,
    apiHostOverride
  );
}

export interface WhitelistIpResult {
  server: string;
  ip?: string;
  error?: string;
}

/** POST /auth/admin/rate-limit/whitelist + /api/admin/rate-limit/whitelist on every API server (admin
 *  only): exempt the admin's current IP from rate limiting until each server restarts — e.g. to run
 *  the e2e suite against production. Never throws; returns one result per server. */
export async function adminWhitelistMyIp(auth: AuthData, magic: string): Promise<WhitelistIpResult[]> {
  const path = `/rate-limit/whitelist?magic=${encodeURIComponent(magic)}`;
  const servers = await adminGetApiServers(auth, magic);
  const targets: [string, () => Promise<{ ip: string }>][] = [
    ['auth', () => authenticatedRequest(auth, 'POST', 'auth', `/auth/admin${path}`, false, {})],
    // No configured servers (local dev): just the default API host.
    ...(servers.length ? servers : [undefined]).map(s => [s?.name ?? 'api', () =>
      authenticatedRequest<{ ip: string }>(auth, 'POST', 'api', `/api/admin${path}`, false, {}, undefined, s?.url)] as [string, () => Promise<{ ip: string }>]),
  ];
  return Promise.all(targets.map(([server, f]) => f().then(
    r => ({ server, ip: r.ip }),
    (e: any) => ({ server, error: e?.message || String(e) }))));
}

/**
 * PUT /api/admin/user/:userId - Update user (admin only)
 * Requires magic authentication parameter. The user record lives on its jurisdiction instance (EU
 * users are only on e1), so pass apiHostOverride to target that server; omit to use the default host.
 */
export async function adminUpdateUser(auth: AuthData, userId: string, update: Partial<Pick<RestUser, 'userStatus' | 'userPlan' | 'nickName'>>, magic: string, apiHostOverride?: string): Promise<RestUser> {
  return await authenticatedRequest<RestUser>(
    auth,
    'PUT',
    'api',
    `/api/admin/user/${encodeURIComponent(userId)}?magic=${encodeURIComponent(magic)}`,
    false,
    update,
    undefined,
    apiHostOverride
  );
}

/** GET /admin/invitations - list all invitations (auth service, admin only). */
export async function adminListInvitations(auth: AuthData, magic: string): Promise<RestInvitation[]> {
  return await authenticatedRequest<RestInvitation[]>(
    auth, 'GET', 'auth',
    `/auth/admin/invitations?appId=downread&magic=${encodeURIComponent(magic)}`,
    false
  );
}

/** POST /admin/invitation - create an invitation (auth service, admin only). */
export async function adminCreateInvitation(auth: AuthData, invitation: Omit<RestInvitationCreate, 'magic' | 'appId'>, magic: string): Promise<RestInvitation> {
  return await authenticatedRequest<RestInvitation>(
    auth, 'POST', 'auth', `/auth/admin/invitation`, false,
    { ...invitation, appId: 'downread', magic }
  );
}

/** PUT /admin/invitation/:id - update an invitation (auth service, admin only). */
export async function adminUpdateInvitation(auth: AuthData, id: string, update: Omit<RestInvitationUpdate, 'magic'>, magic: string): Promise<RestInvitation> {
  return await authenticatedRequest<RestInvitation>(
    auth, 'PUT', 'auth', `/auth/admin/invitation/${encodeURIComponent(id)}`, false,
    { ...update, magic }
  );
}

/** DELETE /admin/invitation/:id - delete an invitation (auth service, admin only). */
export async function adminDeleteInvitation(auth: AuthData, id: string, magic: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth, 'DELETE', 'auth',
    `/auth/admin/invitation/${encodeURIComponent(id)}?magic=${encodeURIComponent(magic)}`,
    false
  );
}

/**
 * PUT /api/admin/user/:userId/capabilities - Set user's downread capabilities (admin only)
 * Requires magic authentication parameter
 */
export async function adminSetUserCapabilities(auth: AuthData, userId: string, userType: string, magic: string, apiHostOverride?: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth,
    'PUT',
    'api',
    `/api/admin/user/${encodeURIComponent(userId)}/capabilities?magic=${encodeURIComponent(magic)}`,
    false,
    { userType },
    undefined,
    apiHostOverride
  );
}

/**
 * DELETE /api/admin/user/:userId - Hard-delete a user (admin only, API host).
 * Server-side teardown: unsubscribes the target from every feed (decrementing counters, removing
 * owned newsletter feeds), deletes their read markers / feed references / user row, then asks the
 * auth service to delete the account. The admin equivalent of the self-delete on the Account page.
 */
export async function adminDeleteUser(auth: AuthData, userId: string, magic: string, apiHostOverride?: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth,
    'DELETE',
    'api',
    `/api/admin/user/${encodeURIComponent(userId)}?magic=${encodeURIComponent(magic)}`,
    false,
    undefined,
    undefined,
    apiHostOverride
  );
}

/**
 * PUT /admin/account - Update account (admin only, auth service)
 * Requires magic authentication parameter
 */
export interface DebugFeedResult {
  success: boolean;
  stderr: string;
  result?: any;
  /** The request line the sync-server handed to the feed reader. */
  request?: string;
}

/**
 * Run the feed reader against a URL. Pass `feedId` instead to replay the request the fetch job
 * would make for that feed (its stored ETag/Last-Modified and proxy hint, on the instance that
 * polls it) — the only way to reproduce a failure caused by that stored state.
 */
export async function adminDebugFeed(auth: AuthData, url: string, magic: string, feedId?: string): Promise<DebugFeedResult> {
  return await authenticatedRequest<DebugFeedResult>(
    auth, 'POST', 'api', `/api/admin/feed/debug?magic=${encodeURIComponent(magic)}`, false,
    feedId ? { feedId } : { url }, 300_000
  );
}

/**
 * POST /admin/account - Create a confirmed auth account directly (admin only, auth service).
 * The account skips the email-confirmation flow and takes an explicit data-residency jurisdiction.
 * Create the matching Downread user with adminCreateUser afterwards.
 */
export async function adminCreateAccount(auth: AuthData, email: string, password: string, jurisdiction: Jurisdiction, magic: string, tag?: string): Promise<RestAdminAccountResult> {
  return await authenticatedRequest<RestAdminAccountResult>(
    auth, 'POST', 'auth', `/auth/admin/account`, false,
    { email, password, jurisdiction, magic, ...(tag ? { tag } : {}) }
  );
}

/**
 * POST /api/admin/user - Create the Downread user record for an admin-created account (admin only).
 * apiHostOverride targets the API instance for the account's jurisdiction so the user's PII lands in
 * the correct region (EU accounts must be created on an EU instance); omit to use the default server.
 */
export async function adminCreateUser(auth: AuthData, accountId: string, jurisdiction: Jurisdiction, magic: string, apiHostOverride?: string, tag?: string): Promise<RestUser> {
  return await authenticatedRequest<RestUser>(
    auth, 'POST', 'api', `/api/admin/user?magic=${encodeURIComponent(magic)}`, false,
    { accountId, jurisdiction: jurisdiction.toLowerCase(), ...(tag ? { tag } : {}) },
    undefined, apiHostOverride
  );
}

export async function adminUpdateAccount(auth: AuthData, accountId: string, update: { userType?: 'user' | 'super', accountStatus?: AccountStatus }, magic: string): Promise<RestAdminAccountResult> {
  return await authenticatedRequest<RestAdminAccountResult>(
    auth,
    'PUT',
    'auth',
    `/auth/admin/account`,
    false,
    { ...update, accountId, magic }
  );
}

/**
 * DELETE /admin/account/:id - hard-delete an auth account (admin only).
 * Use to clean up orphaned auth-only accounts whose downread user has already been removed.
 */
export async function adminDeleteAccount(auth: AuthData, accountId: string, magic: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth,
    'DELETE',
    'auth',
    `/auth/admin/account/${encodeURIComponent(accountId)}?magic=${encodeURIComponent(magic)}`,
    false
  );
}

/**
 * PUT /auth/admin/account/:id/ban - permanently ban a live account (auth service, admin only):
 * suspends it, invalidates its session, and writes a deletedUsers tombstone blocking re-registration.
 * Lift a ban with adminUpdateDeletedUser(account.email, 'none', ...) (see the Retention page).
 */
export async function adminBanAccount(auth: AuthData, accountId: string, banReason: string | undefined, magic: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth, 'PUT', 'auth', `/auth/admin/account/${encodeURIComponent(accountId)}/ban`, false,
    { ...(banReason ? { banReason } : {}), magic }
  );
}

/**
 * GET /admin/tags - list all tags in use with their account counts (auth service, admin only).
 * The auth service is the source of truth for browsing tags (every signup creates an auth account).
 */
export async function adminListTags(auth: AuthData, magic: string): Promise<RestTagSummary[]> {
  return await authenticatedRequest<RestTagSummary[]>(
    auth, 'GET', 'auth', `/auth/admin/tags?magic=${encodeURIComponent(magic)}`, false
  );
}

/** POST /admin/delete-tag - delete all auth accounts carrying a tag. Returns the number of rows removed. */
export async function adminDeleteTagAuth(auth: AuthData, tag: string, magic: string): Promise<number> {
  return await authenticatedRequest<number>(
    auth, 'POST', 'auth', `/auth/admin/delete-tag?magic=${encodeURIComponent(magic)}`, false, { tag, magic }
  );
}

// --- Retention / legal-hold admin (auth service; see downread/RETENTION-CONCEPT.md) ---------

/** GET /admin/holds - list legal-hold records. activeOnly hides holds whose evidence was released. */
export async function adminListHolds(auth: AuthData, magic: string, activeOnly: boolean): Promise<RestHoldRecord[]> {
  return await authenticatedRequest<RestHoldRecord[]>(
    auth, 'GET', 'auth', `/auth/admin/holds?magic=${encodeURIComponent(magic)}${activeOnly ? '&active=1' : ''}`, false
  );
}

/** PUT /admin/hold/:reportId - toggle the law-enforcement extension that suspends a hold's release. */
export async function adminUpdateHold(auth: AuthData, reportId: string, lawEnforcementHold: boolean, magic: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth, 'PUT', 'auth', `/auth/admin/hold/${encodeURIComponent(reportId)}`, false, { lawEnforcementHold, magic }
  );
}

/** GET /admin/deleted-users - list the deletedUsers tombstones (bans + strike carry-forward). */
export async function adminListDeletedUsers(auth: AuthData, magic: string): Promise<RestDeletedUser[]> {
  return await authenticatedRequest<RestDeletedUser[]>(
    auth, 'GET', 'auth', `/auth/admin/deleted-users?magic=${encodeURIComponent(magic)}`, false
  );
}

/** PUT /admin/deleted-user/:emailHash - re-evaluate a ban (e.g. lift on appeal, or ban permanently). */
export async function adminUpdateDeletedUser(auth: AuthData, emailHash: string, banStatus: string, magic: string, banReason?: string): Promise<{ success: boolean }> {
  return await authenticatedRequest<{ success: boolean }>(
    auth, 'PUT', 'auth', `/auth/admin/deleted-user/${encodeURIComponent(emailHash)}`, false,
    { banStatus, ...(banReason != null ? { banReason } : {}), magic }
  );
}

/** GET /admin/retention-log - the accountability log of retention decisions (read-only). */
export async function adminListRetentionLog(auth: AuthData, magic: string): Promise<RestRetentionLogEntry[]> {
  return await authenticatedRequest<RestRetentionLogEntry[]>(
    auth, 'GET', 'auth', `/auth/admin/retention-log?magic=${encodeURIComponent(magic)}`, false
  );
}

/** GET /api/admin/tags - list distinct tags + user counts on ONE API instance (admin only). Users
 *  are sharded per instance (EU users live only on e1), so the Manage Tags page queries the selected
 *  instance to browse its tags; apiHostOverride targets one, omit for the default host. */
export async function adminListTagsApi(auth: AuthData, magic: string, apiHostOverride?: string): Promise<RestTagSummary[]> {
  return await authenticatedRequest<RestTagSummary[]>(
    auth, 'GET', 'api', `/api/admin/tags?magic=${encodeURIComponent(magic)}`, false, undefined, undefined, apiHostOverride
  );
}

/** POST /api/admin/delete-tag - delete all downread users carrying a tag on ONE API instance. Returns
 *  the number of rows removed. Users are sharded per instance (EU users live only on e1), so the tag
 *  must be purged on every instance; apiHostOverride targets one, omit for the default host.
 *  validateAdminRequest reads magic from the query string, while checkAdminDelete reads it from the body, so send both. */
export async function adminDeleteTagApi(auth: AuthData, tag: string, magic: string, apiHostOverride?: string): Promise<number> {
  return await authenticatedRequest<number>(
    auth, 'POST', 'api', `/api/admin/delete-tag?magic=${encodeURIComponent(magic)}`, false, { tag, magic }, undefined, apiHostOverride
  );
}

/**
 * GET /api/admin/notifications - Get admin notifications count
 */
export async function getAdminNotifications(auth: AuthData, magic: string): Promise<RestAdminNotifications> {
  return await authenticatedRequest<RestAdminNotifications>(
    auth,
    'GET',
    'api',
    `/api/admin/notifications`,
    false,
    { magic }
  );
}

/**
 * GET /api/admin/reports - list content reports for review (admin only).
 * status defaults to PENDING server-side; pass 'ALL' or a specific status to override.
 */
export async function getAdminReports(auth: AuthData, magic: string, status?: ReportStatus | 'ALL', last?: string): Promise<RestList<RestReport>> {
  return await authenticatedRequest<RestList<RestReport>>(
    auth,
    'GET',
    'api',
    `/api/admin/reports`,
    false,
    { magic, ...(status ? { status } : {}), ...(last ? { last } : {}) }
  );
}

/**
 * GET /api/admin/reports/personal - reporter PII for reports owned by a specific instance (admin
 * only). The report list from the default server marks cross-instance reports with complete=false
 * and the owning `instance`; this fetches their PII from that instance via apiHostOverride. Returns
 * rows only for ids the targeted instance owns.
 */
export async function getAdminReportsPersonal(auth: AuthData, magic: string, ids: string[], apiHostOverride: string): Promise<RestReportPersonal[]> {
  return await authenticatedRequest<RestReportPersonal[]>(
    auth,
    'GET',
    'api',
    `/api/admin/reports/personal`,
    false,
    { magic, ids: ids.join(',') },
    undefined,
    apiHostOverride
  );
}

/**
 * PUT /api/admin/report/:id - resolve a content report (admin only).
 */
export async function updateAdminReport(auth: AuthData, magic: string, id: string, update: RestReportUpdate): Promise<RestReport> {
  return await authenticatedRequest<RestReport>(
    auth,
    'PUT',
    'api',
    `/api/admin/report/${encodeURIComponent(id)}?magic=${encodeURIComponent(magic)}`,
    false,
    update
  );
}

/** The Yellowfeeds API (downread/yellowfeeds-api), whose admin endpoints take Downread admins' JWTs. */
export const YELLOWFEEDS_API_HOST = process.env.REACT_APP_YELLOWFEEDS_API_HOST || 'https://yfapi01.yellowfeeds.com';

/** The size of Yellowfeeds' review queue: submitted claims and open listing changes of publishers. */
export async function getYellowfeedsQueueCount(auth: AuthData, magic: string): Promise<{ claimCount: number, changeCount: number }> {
  return await authenticatedRequest(auth, 'GET', 'api', `/api/admin/queue/count?magic=${encodeURIComponent(magic)}`,
    false, undefined, undefined, YELLOWFEEDS_API_HOST);
}
